/**
 * Bottom navigation for phones.
 *
 * The desktop home screen is a keyboard launcher; a thumb cannot use it.
 * On a phone the owner is checking outstanding, looking someone up, or
 * recording a single receipt -- so those get permanent, thumb-reachable
 * tabs instead.
 */
import { useLocation, useNavigate } from 'react-router-dom'
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material'
import HomeIcon from '@mui/icons-material/HomeOutlined'
import ReceiptIcon from '@mui/icons-material/ReceiptLongOutlined'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined'
import { useTranslation } from 'react-i18next'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useGuard } from '../store/UnsavedChanges'
import { c } from '../theme'

const ITEMS = [
  { path: '/', icon: <HomeIcon />, key: 'nav.home', perm: null },
  { path: '/outstanding', icon: <AccountBalanceWalletIcon />,
    key: 'nav.outstanding', perm: P.reportOperational },
  { path: '/vouchers/receipt', icon: <ReceiptIcon />,
    key: 'voucher.receipt', perm: P.postReceipt },
  { path: '/daybook', icon: <MenuBookIcon />,
    key: 'nav.dayBook', perm: P.reportOperational },
] as const

export default function BottomNav() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const nav = useNavigate()
  const loc = useLocation()
  const guard = useGuard()

  const items = ITEMS.filter((i) => !i.perm || can(i.perm))
  const active = items.findIndex((i) =>
    i.path === '/' ? loc.pathname === '/' : loc.pathname.startsWith(i.path))

  return (
    <Paper elevation={0} sx={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200,
      borderTop: `1px solid ${c.border}`,
      // Clears the home indicator on notched phones.
      pb: 'env(safe-area-inset-bottom)',
      display: { xs: 'block', md: 'none' },
    }}>
      <BottomNavigation value={active} showLabels sx={{ height: 58 }}
        onChange={(_, i) => guard(() => nav(items[i].path))}>
        {items.map((i) => (
          <BottomNavigationAction key={i.path} label={t(i.key)} icon={i.icon}
            sx={{ minWidth: 0, '& .MuiBottomNavigationAction-label': {
              fontSize: 10.5, mt: 0.25 } }} />
        ))}
      </BottomNavigation>
    </Paper>
  )
}
